<template>
  <div aria-roledescription="carousel" class="Map-restaurant-slider">
    <ux-atoms-typo as="h1" class="accessibility-sr-only" v-if="label">
      {{ label }}
    </ux-atoms-typo>
    <div class="Map-restaurant-slider__container" v-if="cards?.length && sliderIsLoaded">
      <Carousel
        :id="uniqueId"
        :items-to-scroll="1"
        :items-to-show="1"
        @init="initId"
        @slide-start="updateId"
        aria-live="off"
        class="Map-restaurant-slider__carousel"
        ref="carousel"
        snap-align="center"
      >
        <Slide :key="card.id" class="Map-restaurant-slider__slide" v-for="(card, index) in restaurantsCards">
          <ux-atoms-button
            :aria-controls="uniqueSwipeModalId"
            :aria-expanded="isOpen"
            @click="openSwipeModal($event, true, index)"
            class="Map-restaurant-slider__slide-button"
            size="s"
            v-if="isClustered(card.id)"
            variant="tertiary"
          >
            <span class="Map-restaurant-slider__slide-badge">{{ getCluster(card.id)?.length }}</span>
            {{ $t('ux.molecules.mapRestaurantSlider.label') }}
            <ux-atoms-icon name="arrow-up" size="s" />
          </ux-atoms-button>
          <ux-molecules-card-restaurant
            v-bind="card"
            :prevent-redirect="isClustered(card.id)"
            :variant="VariantType.Map"
            @card-restaurant::interact="openSwipeModal($event, isClustered(card.id), index)"
          />
        </Slide>
      </Carousel>
      <swipe-modal
        :aria-labelledby="swipeModalUniqueId"
        :id="uniqueSwipeModalId"
        :is-backdrop="false"
        :is-drag-handle="true"
        :is-full-screen="false"
        :is-persistent="false"
        :is-scroll-lock="false"
        :snap-point="modalSize"
        :style="`--modalMaxHeight: ${modalMaxHeight};`"
        aria-modal="true"
        ref="modalDropdown"
        role="dialog"
        v-model="isOpen"
      >
        <template #drag-handle>
          <div class="Swipe-modal__drag-handle">
            <ux-atoms-typo :id="swipeModalUniqueId" :text="getClusterLabel" as="p" color="dark" variant="text-heading-01" />
            <ux-atoms-button :no-border="true" @click="close" @keydown.enter="close" class="Swipe-modal__close-button" type="button" variant="action">
              <ux-atoms-icon :description="$t('ux.molecules.modal.close.label')" name="close" size="m" />
            </ux-atoms-button>
          </div>
        </template>
        <ux-molecules-grid-card-restaurant
          :cards="getCluster(currentRestaurant.id)"
          :columns="2"
          page-name="restaurantsandbars::results::map"
          v-if="isTablet"
        />
        <ul class="Swipe-modal__list" v-else>
          <li :key="card.id" v-for="card in getCluster(currentRestaurant.id)">
            <ux-molecules-card-restaurant v-bind="card" @card-restaurant::interact="cardEvent($event)" variant="map" />
          </li>
        </ul>
      </swipe-modal>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useWindowSize } from '@vueuse/core';
import { Carousel, Slide } from 'vue3-carousel';

import { VariantType, type cardType } from '~/components/ux/molecules/CardRestaurant/types';
import { addTrapFocusToElement, focusableElements, removeTrapFocusToElement } from '~/helpers/accessibility';
import { gtmEvent } from '~/helpers/gtm';

export interface MapRestaurantSliderProps {
  cards: cardType[][] | null;
  label?: null | string;
}

const props = withDefaults(defineProps<MapRestaurantSliderProps>(), {
  cards: undefined,
  label: undefined
});

const restaurantsCards = computed(() => {
  return props.cards?.flat();
});

const store = useMapRestaurantsStore();
const uniqueId = useId();
const swipeModalUniqueId = useId();
const carousel = ref();
const modalDropdown = ref();
const isOpen = ref(false);
const uniqueSwipeModalId = useId();
const lastFocusElement = ref();
const sliderIsLoaded = ref(true);
const { direction, isSwiping } = useSwipe(carousel);

const { isMobile, isTablet } = useCurrentWindowSize();

const { height } = useWindowSize();

// matches mobile header nav/logo +search bar height
const headerHeight = 134;

const modalSize = computed(() => {
  return isTablet.value ? '50%' : modalMaxHeight.value;
});

const modalMaxHeight = computed(() => {
  return isMobile.value && height.value >= 620 ? `${620 - headerHeight}px` : `${height.value - headerHeight}px`;
});

const { t } = useI18n();
const currentRestaurant = computed(() => {
  return store.selectedCluster;
});

const reloadSlider = () => {
  sliderIsLoaded.value = false;

  nextTick(() => {
    sliderIsLoaded.value = true;
  });
};

const goToSlide = (slideNumber: number) => {
  carousel.value.slideTo(slideNumber);
};

const focusRestaurant = (restaurantCode: string) => {
  const restaurantElement = document.getElementById(restaurantCode);
  const restaurantElementFocus = restaurantElement?.querySelectorAll(focusableElements)[0] as HTMLElement;
  restaurantElementFocus?.focus();
};

const initId = () => {
  if (restaurantsCards.value) {
    store.updateSelectedClusterId(restaurantsCards.value[0].id, '');
  }
};

const getClusterCache = reactive(new Map());

const getCluster = (restaurantCode: string) => {
  const cluster = props.cards?.find((cluster) => cluster.some((restaurant) => restaurant.id === restaurantCode)) || [];

  if (getClusterCache.has(restaurantCode)) {
    if (cluster.length !== getClusterCache.get(restaurantCode).length) {
      getClusterCache.set(restaurantCode, cluster);
    }

    return getClusterCache.get(restaurantCode);
  }

  getClusterCache.set(restaurantCode, cluster);
  return cluster;
};

const isClustered = (restaurantCode: string) => getCluster(restaurantCode).length > 1;

const getClusterLabel = computed(() => {
  const count = getCluster(currentRestaurant.value.id)?.length;
  return t('ux.molecules.mapRestaurantSlider.label', { count });
});

const updateId = (data: { currentSlideIndex: number; prevSlideIndex: number; slidesCount: number; slidingToIndex: number }) => {
  if (restaurantsCards.value) {
    const clusterId = getCluster(restaurantsCards.value[data.slidingToIndex].id)?.[0].id;
    if (clusterId) {
      store.updateSelectedClusterId(clusterId, '');
    }
  }
};

const cardEvent = (cardTitle: string) => {
  gtmEvent('bloc_interact', {
    bloc_interaction: `select - ${cardTitle.toLowerCase()}`,
    bloc_name: 'restaurant list',
    pagename: 'restaurantsandbars::results::map'
  });
};

const openSwipeModal = (e: Event, isCluster: Boolean, slideIndex: number) => {
  if (!isCluster) {
    cardEvent(e);
  }

  if (isCluster && carousel.value.data.currentSlide.value === slideIndex) {
    lastFocusElement.value = e.target;
    isOpen.value = true;
  }
};

const close = () => {
  isOpen.value = false;
  useFocus(lastFocusElement.value);
};

onClickOutside(modalDropdown, () => {
  nextTick(() => {
    close();
  });
});

watch(
  () => restaurantsCards.value,
  () => reloadSlider()
);

watch(
  () => isOpen.value,
  (value) => {
    const modal = modalDropdown.value?._.refs.modalRef;
    if (value) {
      nextTick(() => {
        setTimeout(() => {
          addTrapFocusToElement(modal);
          const firstFocusableElement = modalDropdown.value?.$el?.querySelectorAll(focusableElements)[0] as HTMLElement;
          if (firstFocusableElement) {
            firstFocusableElement.focus();
          }
        }, 50);
      });
    } else {
      removeTrapFocusToElement(modal);
    }
  }
);

watch(
  () => currentRestaurant.value,
  (currentRestaurant) => {
    if (currentRestaurant.action === 'scroll') {
      const index = restaurantsCards.value?.findIndex((card) => {
        return card.id === currentRestaurant.id;
      });
      if (index !== undefined) {
        goToSlide(index);
        setTimeout(() => {
          focusRestaurant(currentRestaurant.id as string);
        }, 500);
      }
    }
  },
  { deep: true }
);

watch(
  () => isSwiping.value,
  (value) => {
    if (value && direction.value === 'up' && isClustered(currentRestaurant.value.id)) {
      isOpen.value = true;
    }
  }
);
</script>
<style lang="scss" scoped>
@use 'MapRestaurantSlider.scss';
</style>
